import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Paper,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import { toast } from 'react-toastify'
import { CnpjMask, onlyNumbers } from '../lib/mask'
import { getVoucherCount, registerVoucher } from '../service/voucherService'

const useStyles = makeStyles(() => ({
  page: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#E5E5E5'
  },
  container: {
    padding: '24px',
    borderRadius: '16px',
    maxWidth: '510px',
    width: '100%',
    backgroundColor: '#ffffff',
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
    boxSizing: 'border-box'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    fontFamily: 'Roobert-Regular, sans-serif',
    color: '#1D1D1D'
  },
  subtitle: {
    fontSize: '12px',
    fontWeight: '400',
    fontFamily: 'Roobert-Regular, sans-serif',
    color: '#6B6B6B',
    marginTop: '20px',
    marginBottom: '16px'
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    fontFamily: 'Roobert-Regular, sans-serif',
    color: '#1D1D1D',
    marginBottom: '8px'
  },
  textFieldFull: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '20px',
      height: '48px',
      padding: '0 12px',
      fontFamily: 'Roobert-Regular, sans-serif',
      fontSize: '14px',
      color: '#1D1D1D',
      '&:hover fieldset': { borderColor: '#3F51B5' },
      '&.Mui-focused fieldset': {
        borderColor: '#3F51B5',
        boxShadow: '0 0 8px rgba(63, 81, 181, 0.4)'
      }
    }
  },
  saveButton: {
    borderRadius: '20px',
    backgroundColor: '#0045B5',
    fontFamily: 'Roobert-Regular, sans-serif',
    fontWeight: '500',
    color: '#ffffff',
    textTransform: 'none',
    height: '48px',
    '&:hover': { backgroundColor: '#0045B5' }
  },
  disabledSaveButton: {
    backgroundColor: '#E0E0E0',
    color: '#9E9E9E'
  }
}))

const VoucherForm = () => {
  const classes = useStyles()

  const [cnpj, setCnpj] = useState('')
  const [voucher, setVoucher] = useState('')
  const [voucherCount, setVoucherCount] = useState(null)
  const [isVoucherFieldEnabled, setIsVoucherFieldEnabled] = useState(false)
  const [isLoadingCount, setIsLoadingCount] = useState(false)

  const isValidCnpj = onlyNumbers(cnpj).length === 14
  const isValidVoucher = voucher.trim().length > 0
  const isFormValid = isValidCnpj && isValidVoucher

  const showVoucherMessage = (message) => {
    const msg = message?.toLowerCase() || ''

    if (msg.includes('utilizado')) {
      toast.warn(`⚠️ ${message}`)
    } else if (msg.includes('não existe')) {
      toast.error(`❌ ${message}`)
    } else {
      toast.error(`❌ ${message || 'Erro ao consumir voucher'}`)
    }
  }

  useEffect(() => {
    const clean = onlyNumbers(cnpj)
    if (clean.length === 14) {
      setIsLoadingCount(true)
      setVoucher('')
      setIsVoucherFieldEnabled(false)

      getVoucherCount(clean)
        .then((qtd) => {
          setVoucherCount(qtd)
          setIsVoucherFieldEnabled(true)
        })
        .catch(() => {
          toast.error('Erro ao buscar quantidade de vouchers')
        })
        .finally(() => {
          setIsLoadingCount(false)
        })
    } else {
      setVoucherCount(null)
      setIsVoucherFieldEnabled(false)
      setVoucher('')
    }
  }, [cnpj])

  const handleCnpjChange = (e) => {
    const raw = onlyNumbers(e.target.value).slice(0, 14)
    setCnpj(CnpjMask(raw))
  }

  const handleSave = async () => {
    const cleanVoucher = voucher.trim().toLowerCase()
    const cleanCnpj = onlyNumbers(cnpj)

    setIsLoadingCount(true)

    try {
      const result = await registerVoucher({
        voucherCode: cleanVoucher,
        cnpj: cleanCnpj
      })

      if (!result.success) {
        showVoucherMessage(result.message)
        return
      }

      toast.success('✅ Cadastro realizado com sucesso!')
      setVoucher('')

      const updated = await getVoucherCount(cleanCnpj)
      setVoucherCount(updated)
    } catch (err) {
      showVoucherMessage(err.message)
    } finally {
      setIsLoadingCount(false)
      setIsVoucherFieldEnabled(true)
    }
  }

  return (
    <Paper className={classes.container}>
      <Typography className={classes.title}>Voucher – Formulário</Typography>
      <Divider />
      <Typography className={classes.subtitle}>* Dados obrigatórios</Typography>

      <Box>
        <Typography className={classes.label}>CNPJ da Loja Participante *</Typography>
        <TextField
          variant="outlined"
          value={cnpj}
          onChange={handleCnpjChange}
          className={classes.textFieldFull}
        />
      </Box>

      {voucherCount !== null || isLoadingCount ? (
        <>
          <Box mt={2}>
            <Typography className={classes.label}>Nº do Voucher *</Typography>
            <TextField
              variant="outlined"
              value={voucher}
              onChange={(e) => setVoucher(e.target.value)}
              className={classes.textFieldFull}
              disabled={!isVoucherFieldEnabled || isLoadingCount}
              inputProps={{ maxLength: 5 }}
            />
          </Box>

          {/*<Box mt={2}>
            <Typography className={classes.label}>
              {isLoadingCount ? (
                'Carregando quantidade de vouchers...'
              ) : (
                <>
                  Quantidade de Vouchers Cadastrados para essa loja:{' '}
                  <span style={{ color: '#0045B5', fontWeight: '600' }}>{voucherCount}</span>
                </>
              )}
            </Typography>
          </Box>*/}
        </>
      ) : null}

      <Box mt={3}>
        <Button
          fullWidth
          className={`${classes.saveButton} ${!isFormValid ? classes.disabledSaveButton : ''}`}
          onClick={handleSave}
          disabled={!isFormValid}
        >
          Cadastrar Voucher
        </Button>
      </Box>
    </Paper>
  )
}

export default VoucherForm
